import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GameFrame, BetControls } from "@/components/GameFrame";
import { useAudio } from "@/context/GameFeel";

const SYMBOLS = { cherry: "🍒", lemon: "🍋", bell: "🔔", star: "⭐", seven: "7️⃣", diamond: "💎" };
const ALL = ["cherry", "lemon", "bell", "star", "seven", "diamond"];

export default function SlotGame() {
  const { user, setUser } = useAuth();
  const { react } = useAudio();
  const [bet, setBet] = useState(100);
  const [reels, setReels] = useState(["seven", "seven", "seven"]);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);

  const spin = async () => {
    if (bet > (user?.coins ?? 0)) return toast.error("Not enough coins");
    setSpinning(true);
    setResult(null);
    const interval = setInterval(() => {
      setReels([rand(), rand(), rand()]);
    }, 80);
    try {
      const { data } = await api.post("/games/slot/spin", { bet });
      await new Promise((r) => setTimeout(r, 900));
      clearInterval(interval);
      setReels(data.reels);
      setResult(data);
      setUser((u) => ({ ...u, coins: data.coins }));
      if (data.payout > 0) toast.success(`WIN! +${data.payout.toLocaleString()} coins (${data.multiplier}x)`);
      else toast(`No win — try again!`);
      react({ outcome: data.payout > 0 ? "win" : "loss", game: "slot", big: data.multiplier >= 10 });
    } catch (e) {
      clearInterval(interval);
      toast.error(apiErr(e));
    } finally {
      setSpinning(false);
    }
  };
  const rand = () => ALL[Math.floor(Math.random() * ALL.length)];

  return (
    <GameFrame title="Neon Slots" subtitle="Match three to win up to 50x"
      side={<>
        <BetControls bet={bet} setBet={setBet} coins={user?.coins} disabled={spinning} onPlay={spin} playLabel={spinning ? "Spinning…" : "SPIN"} testid="spin" />
        <div className="glass rounded-2xl p-5 border border-white/10">
          <p className="text-sm font-semibold mb-3">Paytable (3 of a kind)</p>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {Object.entries({ diamond: 50, seven: 25, star: 10, bell: 6, lemon: 4, cherry: 3 }).map(([s, m]) => (
              <div key={s} className="flex items-center justify-between bg-black/30 rounded-lg px-3 py-1.5">
                <span>{SYMBOLS[s]}</span><span className="font-mono text-amber-400">{m}x</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-3">Any two matching pays 2x.</p>
        </div>
      </>}>
      <div className="glass rounded-3xl p-8 border border-amber-500/20 glow-coins flex flex-col items-center">
        <div className="flex gap-4 mb-6">
          {reels.map((s, i) => (
            <motion.div key={i} animate={spinning ? { y: [0, -8, 0] } : {}} transition={{ repeat: spinning ? Infinity : 0, duration: 0.2 }}
              className="w-24 h-28 sm:w-28 sm:h-32 rounded-2xl bg-black/50 border-2 border-amber-500/30 flex items-center justify-center text-6xl shadow-inner">
              {SYMBOLS[s]}
            </motion.div>
          ))}
        </div>
        {result && (
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className={`text-center px-6 py-3 rounded-xl ${result.payout > 0 ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-gray-400"}`}>
            {result.payout > 0 ? `🎉 You won ${result.payout.toLocaleString()} coins!` : "No match this time"}
          </motion.div>
        )}
      </div>
    </GameFrame>
  );
}
